import { DestroyRef, inject, Injectable } from '@angular/core';
import { Observable, Subject } from 'rxjs';

/** What one tab tells the other tabs of the same app about the login it shares with them. */
export type SessionMessage = 'signed-in' | 'signed-out';

export const SESSION_CHANNEL = 'rc-session';

const SESSION_MESSAGES: readonly SessionMessage[] = ['signed-in', 'signed-out'];

// A BroadcastChannel also reaches other channels of the same name in this tab: the sender's mark lets this tab skip
// its own messages.
const THIS_TAB = crypto.randomUUID();

/** Tells every other tab of this app (same origin) that the user logged in or out here. */
export function announceSession(type: SessionMessage): void {
  if (typeof BroadcastChannel === 'undefined') {
    return;
  }
  const channel = new BroadcastChannel(SESSION_CHANNEL);
  channel.postMessage({ type, from: THIS_TAB });
  channel.close();
}

// Logging in or out in one tab, while the others still show the old state: the tabs share the login (in
// localStorage), so they only need to hear that it changed.
@Injectable({ providedIn: 'root' })
export class SessionSyncService {
  readonly #messages = new Subject<SessionMessage>();
  readonly messages$: Observable<SessionMessage> = this.#messages.asObservable();

  constructor() {
    if (typeof BroadcastChannel === 'undefined') {
      return;
    }
    const channel = new BroadcastChannel(SESSION_CHANNEL);
    channel.onmessage = ({ data }: MessageEvent) => {
      const { type, from } = (data ?? {}) as { type?: SessionMessage; from?: string };
      if (from !== THIS_TAB && type && SESSION_MESSAGES.includes(type)) {
        this.#messages.next(type);
      }
    };
    inject(DestroyRef).onDestroy(() => channel.close());
  }

  announce(type: SessionMessage): void {
    announceSession(type);
  }
}
